import { FC, useEffect, useMemo, useState } from "react";
import { DashboardDevice } from "../../../../../../models/api/DashboardDevice";
import {
  ConfigState,
  HistoricalWidgetMeasurementConfig,
  INITIAL_CONFIG_STATE,
} from "../../../SimpleAppTemplate";
import { ChartCard } from "../../../../../../components/cards";
import { ErrorNoData } from "../../index";
import MeasurementHelpers from "../../../../../../helpers/MeasurementHelpers";
import { useHistoricalChartsFilter } from "../../context/historical-charts-filter";
import { LoadingSkeleton } from "../../../../../../components/LoadingSkeleton";
import { AMCWaterVolumeAndRain } from "../../../../../../components/charts";
import useHistoricalWaterVolumeAndRain from "../hooks/useHistoricalWaterVolumeAndRain";
import { Guid } from "js-guid";
import SupportLink from "../../../../../../helpers/SupportLink";

type Props = {
  device: DashboardDevice;
  orgId?: string;
  chartLabel: string;
  measurements: HistoricalWidgetMeasurementConfig[];
};

const WaterVolumeAndRain: FC<Props> = ({ device, orgId, chartLabel, measurements }) => {
  const icons: { icon: string | JSX.Element; errorIcon: string | JSX.Element } = {
    icon: "",
    errorIcon: "fa-regular fa-chart-column",
  };
  const { duration, resolution, timeLastRefresh, customEndDate } = useHistoricalChartsFilter();
  const [volumeLabel, setVolumeLabel] = useState<string>("Volume");
  const [rainLabel, setRainLabel] = useState<string>("Rain");
  const [configError, setConfigError] = useState<ConfigState>(INITIAL_CONFIG_STATE);
  const {
    fetchData,
    data: historicalChartData,
    isLoading,
    hasError,
    errorMessage,
  } = useHistoricalWaterVolumeAndRain(device.id);

  useEffect(() => {
    if (!!measurements && measurements.length == 2) {
      // Volume Data - Required
      const volumeName = measurements[0].name;
      const volumeFieldKey = measurements[0].fieldKey;

      // Volume Data - Optional
      const volumeDisplayName = measurements[0].displayName;
      const volumeFieldName = measurements[0].fieldName;
      const volumePortNumber = parseInt(measurements[0].port ?? "0");
      const volumeLabel = measurements[0].label;

      // Rain Data - Required
      const rainName = measurements[1].name;
      const rainFieldKey = measurements[1].fieldKey;

      // Rain Data - Optional
      const rainDisplayName = measurements[1].displayName;
      const rainFieldName = measurements[1].fieldName;
      const rainPortNumber = parseInt(measurements[1].port ?? "0");
      const rainLabel = measurements[1].label;

      // Device Data
      const tankArea = device.tankArea;
      const tankDepth = device.tankDepth;

      let hasConfigError = !volumeName || !volumeFieldKey || !rainName || !rainFieldKey;
      if (hasConfigError) {
        setConfigError({
          hasConfigError: true,
          errorMsg: "No Measurement Name and/or FieldKey configured for this widget",
        });
      }

      hasConfigError = !tankArea || !tankDepth;
      if (hasConfigError) {
        setConfigError({
          hasConfigError: true,
          errorMsg: "No Tank Depth and/or Area configured for this device",
        });
      }

      if (volumeLabel) setVolumeLabel(volumeLabel);
      if (rainLabel) setRainLabel(rainLabel);

      hasConfigError =
        !volumeName || !volumeFieldKey || !rainName || !rainFieldKey || !tankArea || !tankDepth;
      if (!hasConfigError) {
        const getData = async () => {
          await fetchData({
            duration,
            resolution,
            customEndDate,
            hoursToAdd: MeasurementHelpers.getDifferenceInHoursFromUTCTime(),
            volumeName,
            volumeFieldKey,
            rainName,
            rainFieldKey,
            tankArea,
            tankDepth,
            volumeDisplayName,
            volumeFieldName,
            volumePortNumber,
            rainDisplayName,
            rainFieldName,
            rainPortNumber,
            orgId,
          });
        };

        getData();
      }
    } else {
      setConfigError({
        hasConfigError: true,
        errorMsg: "We need 2 Measurement Configurations for this widget",
      });

      if (measurements.length > 2) {
        setConfigError({
          hasConfigError: true,
          errorMsg: "Only 2 Measurement Configurations is needed for this widget",
        });
      }
    }
  }, [timeLastRefresh, orgId]);

  const GetDisplayChart = useMemo(() => {
    //If there is config error
    if (configError.hasConfigError) {
      return (
        <ErrorNoData
          cardCaption={`${configError.errorMsg}.`}
          supportQuestion={
            <p>
              Configured correctly?
              <span>&nbsp;</span>
              <SupportLink />
            </p>
          }
          iconClass={icons.errorIcon}
          iconPos="left"
        />
      );
    }

    // If it is still loading
    if (isLoading) {
      return <LoadingSkeleton width="100%" height="500px" />;
    }

    // If there is an error after loading
    if (hasError) {
      return (
        <ErrorNoData
          cardCaption={`${errorMessage}`}
          supportQuestion={
            <p>
              <SupportLink />
            </p>
          }
          iconClass={icons.errorIcon}
          iconPos="left"
        />
      );
    }

    if (historicalChartData.hasData) {
      return (
        <AMCWaterVolumeAndRain
          data={historicalChartData.historicalData}
          volumeLabel={volumeLabel}
          rainLabel={rainLabel}
          chartId={new Guid().toString()}
        />
      );
    } else {
      if (historicalChartData.errorMessage) {
        return (
          <ErrorNoData
            cardCaption={`${historicalChartData.errorMessage}.`}
            supportQuestion={
              <p>
                <SupportLink />
              </p>
            }
            iconClass={icons.errorIcon}
            iconPos="left"
          />
        );
      }

      return (
        <ErrorNoData
          cardCaption={`No data for ${device.name} in this duration.`}
          supportQuestion={
            <p>
              Expecting data?
              <span>&nbsp;</span>
              <SupportLink />
            </p>
          }
          iconClass={icons.errorIcon}
          iconPos="left"
        />
      );
    }
  }, [historicalChartData.historicalData?.length, configError]);

  return (
    <ChartCard
      chartTitle={chartLabel == "" ? "Water Tank Volume x Rain" : chartLabel}
      showFilters={true}
      chart={<>{GetDisplayChart}</>}
    />
  );
};

export default WaterVolumeAndRain;
